// client/src/components/pages/Geofencing/DailyTimeBreakdown.tsx (converted from .js, ST-00228;
// LexiStar visits since ST-00269)
//
// Geofences > Analytics: how long vehicles spent in each zone, per SAST day, with the entries
// and the average stay. Since ST-00269 it reads LexiStar's zone visits
// (GET /api/geofence-events/time-in-zones, from geofencing.visits): the legacy ENTRY / EXIT
// events it used to pair are retired. The server splits every visit over the days and clips
// it to the dates chosen; a vehicle still inside counts up to now, but never beyond its next
// visit to the same zone.
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AxiosError } from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../../services/api';
import { FaChartBar, FaSync } from 'react-icons/fa';
import { useThemeTokens } from '../../../utils/themeTokens';
import { formatMiningDateShort, formatMiningTime, toApiDateString } from '../../../utils/dateUtils';
import { UnifiedDateRangePicker } from '../../../types/commonComponents';
import type { ZoneTimeResponse, ZoneTimeRow } from '../../../types/clientPages/geofencing';

/** The longest window the server reads (days). */
export const MAX_DAYS = 31;

const today = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const errorText = (err: unknown, fallback: string): string =>
  (err as AxiosError<{ error?: string }>)?.response?.data?.error || fallback;

/** "2 h 5 min" / "45 min" / "0 min". */
export function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** The SAST day label of a YYYY-MM-DD day ("19 Oct"). */
const dayLabel = (day: string): string => formatMiningDateShort(`${day}T12:00:00+02:00`);

interface CustomTooltipProps {
  active?: boolean;
  payload?: { payload: ZoneTimeRow }[];
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null;
  const data = payload[0]!.payload;
  return (
    <div className="bg-surface border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-900 mb-1">{data.zone}</p>
      <p className="text-teal-600 font-medium">
        {data.hours} hrs ({formatMinutes(data.minutes)})
      </p>
      {data.vehicles.length > 0 && (
        <div className="mt-1.5 border-t border-gray-100 pt-1.5">
          {data.vehicles.map((v) => (
            <p key={v.regNo} className="text-gray-500 text-xs">
              {v.regNo}: {formatMinutes(v.minutes)}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default function DailyTimeBreakdown() {
  const t = useThemeTokens();
  const [startDate, setStartDate] = useState<Date>(today);
  const [endDate, setEndDate] = useState<Date>(today);
  const [data, setData] = useState<ZoneTimeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const from = toApiDateString(startDate);
  const to = toApiDateString(endDate);

  const fetchTimes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.geofenceEvents.timeInZones({ from, to });
      setData((res.data as ZoneTimeResponse | undefined) ?? null);
    } catch (err) {
      console.error('Error fetching time in zones:', err);
      setError(errorText(err, 'Failed to load the time in zones.'));
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    fetchTimes();
  }, [fetchTimes]);

  const zones = useMemo(() => data?.zones ?? [], [data]);
  const totals = useMemo(
    () => ({
      hours: zones.reduce((s, z) => s + z.hours, 0).toFixed(1),
      minutes: zones.reduce((s, z) => s + z.minutes, 0),
      entries: zones.reduce((s, z) => s + z.entries, 0),
      vehicles: new Set(zones.flatMap((z) => z.vehicles.map((v) => v.regNo))).size,
    }),
    [zones]
  );
  const days = data?.days ?? [];
  const multiDay = days.length > 1;

  const header = (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div className="flex items-center gap-2">
        <FaChartBar className="text-teal-500" aria-hidden="true" />
        <h2 className="text-lg font-semibold text-gray-900">Daily Time Breakdown by Zone</h2>
      </div>
      <div className="flex items-end gap-3">
        <div className="w-full sm:w-64">
          <span className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
            Dates
          </span>
          <UnifiedDateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
        </div>
        <button
          type="button"
          onClick={fetchTimes}
          className="flex items-center gap-1.5 h-10 text-sm text-gray-500 hover:text-teal-600 transition-colors"
        >
          <FaSync size={12} aria-hidden="true" />
          Refresh
        </button>
      </div>
    </div>
  );

  if (loading && !data) {
    return (
      <div className="space-y-6">
        {header}
        <div className="flex items-center justify-center h-64" role="status" aria-label="Loading">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header}

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {error}
          <button type="button" onClick={fetchTimes} className="ml-2 underline font-medium">
            Retry
          </button>
        </div>
      ) : data && !data.available ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-500">
          <FaChartBar className="mx-auto text-3xl text-gray-300 mb-2" aria-hidden="true" />
          <p className="font-medium">Zone visits are not set up on this server yet</p>
          <p className="text-sm mt-1">LexiStar records them once it is installed.</p>
        </div>
      ) : zones.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-500">
          <FaChartBar className="mx-auto text-3xl text-gray-300 mb-2" aria-hidden="true" />
          <p className="font-medium">No zone activity recorded for these dates</p>
          <p className="text-sm mt-1">Time data will appear here once vehicles enter zones.</p>
        </div>
      ) : (
        <>
          {/* Chart */}
          <div className="bg-surface border border-gray-200 rounded-lg p-4">
            <ResponsiveContainer width="100%" height={Math.max(200, zones.length * 50 + 40)}>
              <BarChart
                data={zones}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <XAxis
                  type="number"
                  tick={{ fontSize: 12, fill: t.axisLabel }}
                  tickFormatter={(v: number) => `${v}h`}
                  axisLine={{ stroke: t.grid }}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="zone"
                  width={140}
                  tick={{ fontSize: 12, fill: t.axisLabel }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="hours" fill="#14b8a6" radius={[0, 4, 4, 0]} barSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Summary table */}
          <div className="bg-surface border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700">Summary</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">
                      Zone
                    </th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 uppercase">
                      Time (hrs)
                    </th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 uppercase">
                      Time (min)
                    </th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 uppercase">
                      Entries
                    </th>
                    <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 uppercase">
                      Avg stay
                    </th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">
                      Vehicles
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {zones.map((row) => (
                    <tr key={row.geofenceId} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-900">
                        {row.zone}
                        {row.openVisits > 0 && (
                          <span className="ml-2 text-xs font-normal text-teal-700">
                            {row.openVisits} inside now
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right text-teal-600 font-medium tabular-nums">
                        {row.hours}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-600 tabular-nums">
                        {row.minutes}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-600 tabular-nums">
                        {row.entries}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-600 tabular-nums">
                        {row.avgDwellMinutes == null ? '-' : formatMinutes(row.avgDwellMinutes)}
                      </td>
                      <td className="px-4 py-2 text-gray-500">
                        {row.vehicles.map((v) => `${v.regNo} (${v.minutes}m)`).join(', ') || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-4 py-2 text-gray-700">Total</td>
                    <td className="px-4 py-2 text-right text-teal-700 tabular-nums">
                      {totals.hours}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-700 tabular-nums">
                      {totals.minutes}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-700 tabular-nums">
                      {totals.entries}
                    </td>
                    <td className="px-4 py-2" />
                    <td className="px-4 py-2 text-gray-500">{totals.vehicles} vehicles</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Per day, when the dates span more than one */}
          {multiDay && (
            <div className="bg-surface border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-700">Time per day (hrs)</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">
                        Zone
                      </th>
                      {days.map((d) => (
                        <th
                          key={d.day}
                          className="text-right px-3 py-2 text-xs font-medium text-gray-500 uppercase whitespace-nowrap"
                        >
                          {dayLabel(d.day)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {zones.map((row) => (
                      <tr key={row.geofenceId} className="border-b border-gray-50">
                        <td className="px-4 py-2 font-medium text-gray-900 whitespace-nowrap">
                          {row.zone}
                        </td>
                        {row.days.map((d) => (
                          <td
                            key={d.day}
                            className="px-3 py-2 text-right text-gray-600 tabular-nums"
                          >
                            {d.minutes ? (d.minutes / 60).toFixed(1) : '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 font-semibold">
                      <td className="px-4 py-2 text-gray-700">Total</td>
                      {days.map((d) => (
                        <td key={d.day} className="px-3 py-2 text-right text-gray-700 tabular-nums">
                          {(d.minutes / 60).toFixed(1)}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-500">
            From LexiStar&apos;s zone visits. A vehicle still inside counts up to{' '}
            {data?.asOf ? formatMiningTime(data.asOf) : 'now'}; Avg stay is over the finished visits
            that began in these dates. At most {MAX_DAYS} days at a time.
          </p>
        </>
      )}
    </div>
  );
}
